import { useContext, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import * as H from 'history'
import { ContentBody, ContentHeader } from 'components/shared/content'
import { ExTable, SelectionFixedFooter, TableBody, TableCell, TableHead, TableWrapper, useMultipleCheckForm } from 'components/shared/widget'
import { useDeleteRequestFormIssuanceApi, useDownloadCsvRequestFormIssuancesApi, useFetchRequestFormIssuancesApi } from 'api/request_form_issuance'
import { RequestFormResettableProductType, useFetchAvailableRequestFormCountsApi, usePostUpdateAllRequestFormNotCountedInStockApi } from 'api/institution'
import { RequestFormIssuance, RequestFormIssuanceSearchForm } from 'entities/request_form_issuance'
import { Pagination } from 'components/shared/pagination'
import { useEffectSkipFirst, useForm, useQuery } from 'utils/hooks'
import { SearchConditionView } from 'components/shared/search_drawer'
import { GlobalStateContext } from 'contexts/global_state_context'
import { requestFormIssuanceColumnInfoList, RequestFormIssuanceTableData } from './request_form_issuance_table'
import { TableHeads, useDisplaySetting } from 'components/shared/display_setting'
import qs from 'qs'
import { InputField } from 'components/shared/input'
import { useState } from 'react'
import { Flex } from 'components/shared/flex'
import { Alert, Button, Modal } from 'react-bootstrap'
import { IssueRequestForm, useIssueRequestFormApi } from 'api/institution'
import { CustomerOrderCsvUploadForm } from 'components/customer_order/customer_order_form'
import { useFetchCustomerOrdersApi, usePostCustomerOrderApi } from 'api/customer_order'
import { CustomerOrderForm, CustomerOrderSearchForm } from 'entities/customer_order'
import { useCreateRequestFormsOfCustomerOrderApi } from 'api/customer'
import { RequestFormIssuanceViewModel } from 'view_models/request_form_issuance_view_model'
import moment from 'moment'

type Props = {
    history: H.History
}

const RequestFormIssuancesPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const params = useQuery<RequestFormIssuanceSearchForm & { amount: number; toC: string }>()
    const preSearchForm = useForm<RequestFormIssuanceSearchForm>(params)
    const searchForm = useForm<RequestFormIssuanceSearchForm>({ ...params, ordering: '-request_form_issuance_created_at', perPage: 20 })
    const api = useFetchRequestFormIssuancesApi(searchForm)
    const availableRequestFormCountsApi = useFetchAvailableRequestFormCountsApi(params.institutionId)
    const downloadCsvApi = useDownloadCsvRequestFormIssuancesApi(searchForm)
    const checkIdForm = useMultipleCheckForm<RequestFormIssuance>()
    const requestFormIssuanceDisplaySetting = useDisplaySetting(requestFormIssuanceColumnInfoList(searchForm), 'RequestFormIssuance')
    const [selectedRequestFormIssuance, setSelectedRequestFormIssuance] = useState<RequestFormIssuance>({})
    const [showEdit, setShowEdit] = useState<boolean>(false)
    const addRequestForm = useForm<IssueRequestForm>({ amount: params.amount })
    const issueRequestApi = useIssueRequestFormApi()
    const [showAddRequest, setShowAddRequest] = useState<boolean>(false)
    const [showResetRequest, setShowResetRequest] = useState<boolean>(false)
    const isHomeTest = process.env.REACT_APP_IS_HOME_TEST
    const postForm = useForm<CustomerOrderForm>({ institutionId: params.institutionId })
    const postApi = usePostCustomerOrderApi()
    const resetRequestFormApi = usePostUpdateAllRequestFormNotCountedInStockApi()
    const [showPostForm, setShowPostForm] = useState<boolean>(false)
    const createRequestFormOfCustomerOrderApi = useCreateRequestFormsOfCustomerOrderApi()
    const customerOrdersSearchForm = useForm<CustomerOrderSearchForm>({ institutionId: params.institutionId, perPage: 10000, requestFormsCount: 0 })
    const customerOrdersApi = useFetchCustomerOrdersApi(customerOrdersSearchForm)
    const deleteApi = useDeleteRequestFormIssuanceApi()

    useEffect(() => {
        api.execute()
        customerOrdersApi.execute()
    }, [])

    useEffect(() => {
        globalState.setLoading(postApi.loading)
        if (postApi.isSuccess()) {
            setShowPostForm(false)
            api.execute()
        }
    }, [postApi.loading])

    const handleClickRemoveFilter = (key: keyof RequestFormIssuanceSearchForm): void => {
        searchForm.update((f) => {
            delete f[key]
        })
    }

    useEffect(() => {
        const queryParams = qs.stringify({ ...searchForm.object, page: api.pageSet.page, perPage: api.pageSet.perPage })
        if (props.history.location.search.replace('?', '') !== queryParams) {
            props.history.replace(`request_form_issuances?${queryParams}`)
        }
    }, [searchForm.object])

    const handleClickEdit = (requestFormIssuance: RequestFormIssuance) => {
        setSelectedRequestFormIssuance(requestFormIssuance)
        setShowEdit(true)
    }

    const handleClickResetRequestForm = (productType: RequestFormResettableProductType) => {
        globalState.showConfirm(
            <div>
                利用可能な依頼書残数を0にリセットしますか？
                <br />
                ※1 リセット後に資材を新たに施設に送る場合は手動で発送資材を追加してください。
                <br />
                ※2 リセット対象の依頼書が使用されても問題ございません。
            </div>,
            () => {
                resetRequestFormApi.execute(Number(params.institutionId), productType)
            }
        )
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(downloadCsvApi.loading)
    }, [downloadCsvApi.loading])

    const getShippingCode = (requestFormIssuance: RequestFormIssuance) => {
        const type = requestFormIssuance.institution?.shippingType
        if (type === 'ゆうパック') {
            return 'A'
        } else if (type === '微研') {
            return 'B'
        } else {
            return ''
        }
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(issueRequestApi.loading)
        if (issueRequestApi.isSuccess()) {
            api.execute()
        }
    }, [issueRequestApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(createRequestFormOfCustomerOrderApi.loading)
        if (createRequestFormOfCustomerOrderApi.isSuccess()) {
            customerOrdersApi.execute()
            api.execute()
        }
    }, [createRequestFormOfCustomerOrderApi.loading])

    useEffectSkipFirst(() => {
        if (deleteApi.isSuccess()) {
            api.execute()
        }
    }, [deleteApi.loading])

    useEffectSkipFirst(() => {
        if (resetRequestFormApi.isSuccess()) {
            params.institutionId && availableRequestFormCountsApi.execute(params.institutionId)
            setShowResetRequest(false)
        }
    }, [resetRequestFormApi.loading])

    const today = moment().format('YYYY/MM/DD')

    return (
        <>
            {!isHomeTest ? (
                <>
                    <Modal show={showAddRequest} onHide={() => setShowAddRequest(false)}>
                        <Modal.Header closeButton>
                            <Modal.Title>SCANの検査依頼書の発行</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div>
                                <div>施設名</div>
                                <div style={{ fontSize: 18, marginBottom: 5 }}>{params.institutionName}</div>
                            </div>

                            <InputField form={addRequestForm} attr="amount" required label="発行枚数" type="number" />
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowAddRequest(false)}>
                                キャンセル
                            </Button>
                            <Button
                                variant="success"
                                onClick={() => {
                                    issueRequestApi.execute(Number(params.institutionId), addRequestForm)
                                    setShowAddRequest(false)
                                }}
                            >
                                発行
                            </Button>
                        </Modal.Footer>
                    </Modal>
                    <Modal show={showResetRequest} onHide={() => setShowResetRequest(false)}>
                        <Modal.Header closeButton>
                            <Modal.Title>依頼書残数のリセット</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div style={{ marginBottom: 20 }}>
                                <div>施設が依頼書を紛失した等で、依頼書残数にずれが生じたときに利用します。依頼書残数を0にリセットします。</div>
                            </div>
                            <div>
                                <div style={{ fontSize: 18, marginBottom: 5, fontWeight: 'bold' }}>対象施設名</div>
                                <div style={{ fontSize: 18, marginBottom: 5 }}>{params.institutionName}</div>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: 20 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between' }}>
                                    <div>scan依頼書残数: {availableRequestFormCountsApi.response.scan}</div>
                                    <Button
                                        variant="danger"
                                        onClick={() => handleClickResetRequestForm('scan')}
                                        disabled={availableRequestFormCountsApi.response.scan === 0}
                                    >
                                        scanリセット
                                    </Button>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between' }}>
                                    <div>check依頼書残数: {availableRequestFormCountsApi.response.check}</div>
                                    <Button
                                        variant="danger"
                                        onClick={() => handleClickResetRequestForm('check')}
                                        disabled={availableRequestFormCountsApi.response.check === 0}
                                    >
                                        checkリセット
                                    </Button>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between' }}>
                                    <div>lite依頼書残数: {availableRequestFormCountsApi.response.lite}</div>
                                    <Button
                                        variant="danger"
                                        onClick={() => handleClickResetRequestForm('lite')}
                                        disabled={availableRequestFormCountsApi.response.lite === 0}
                                    >
                                        liteリセット
                                    </Button>
                                </div>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowResetRequest(false)}>
                                閉じる
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </>
            ) : (
                <CustomerOrderCsvUploadForm
                    form={postForm}
                    show={showPostForm}
                    onClose={() => setShowPostForm(false)}
                    onComplete={() => {
                        postApi.execute(postForm)
                    }}
                />
            )}

            <ContentHeader
                headerTitle="検査依頼書"
                left={
                    <>
                        {!isHomeTest && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Button
                                    variant="success"
                                    onClick={() => {
                                        setShowAddRequest(true)
                                    }}
                                >
                                    SCANの検査依頼書を発行
                                </Button>
                                <div
                                    style={{ cursor: 'pointer', color: 'red' }}
                                    onClick={() => {
                                        setShowResetRequest(true)
                                    }}
                                >
                                    依頼書残数リセット
                                </div>
                            </div>
                        )}
                        <SearchConditionView
                            searchForm={searchForm}
                            onClickRemoveButton={handleClickRemoveFilter}
                            hiddenSearchParams={['institutionId', 'amount', 'toC']}
                        />
                    </>
                }
                right={
                    isHomeTest &&
                    params.toC == 'false' && (
                        <Button variant="info" onClick={() => setShowPostForm(true)}>
                            購買データアップロード
                        </Button>
                    )
                }
            />
            <Flex style={{ marginTop: 5 }} alignItems="center" justifyContent="space-between">
                <Flex alignItems="center">
                    <InputField
                        attr="code"
                        form={preSearchForm}
                        placeholder="コードで検索"
                        onPressEnter={() => {
                            searchForm.updateObject('code', preSearchForm.getValue('code'))
                        }}
                        style={{ marginBottom: 0, width: 250 }}
                    />
                </Flex>
                <Flex alignItems="center">
                    <Pagination pageSet={api.pageSet} response={api.response}></Pagination>
                    <div style={{ minWidth: 140, textAlign: 'right' }}>総件数： {api.response.count.toLocaleString()}件</div>
                </Flex>
            </Flex>

            {isHomeTest &&
                params.toC &&
                customerOrdersApi.response.results.filter((co) => co.requestForms?.length === 0 && (co.status === 'paid' || !co.status)).length !== 0 && (
                    <Alert variant="warning" style={{ marginTop: 10 }}>
                        発送作業待ちのオーダーが
                        {customerOrdersApi.response.results
                            .filter((co) => co.requestForms?.length === 0 && (co.status === 'paid' || !co.status) && co.version === 1)
                            .reduce((prev, co) => prev + co.amount!, 0)}
                        件あります。
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => {
                                globalState.showConfirm('toC向けの発送作業を開始しますか？', () => {
                                    createRequestFormOfCustomerOrderApi.execute()
                                })
                            }}
                        >
                            toC向け発送作業開始
                        </Button>
                    </Alert>
                )}
            <ContentBody>
                <TableWrapper showFooter={checkIdForm.form.object.items.length > 0} contentHeaderCount={2}>
                    <ExTable className="sticky-table">
                        <TableHead>
                            <tr>
                                <TableHeads displaySetting={requestFormIssuanceDisplaySetting} />
                                <TableCell th width={80}></TableCell>
                                <TableCell th width={80}></TableCell>
                                {isHomeTest && <TableCell th width={80}></TableCell>}
                            </tr>
                        </TableHead>
                        <TableBody loading={api.loading} columnLength={requestFormIssuanceDisplaySetting.getColumnLength() + (isHomeTest ? 3 : 2)}>
                            {api.response.results.map((requestFormIssuance: RequestFormIssuance) => {
                                const vm = new RequestFormIssuanceViewModel(requestFormIssuance)

                                return (
                                    <tr key={`requestFormIssuance-${requestFormIssuance.id}`}>
                                        <RequestFormIssuanceTableData
                                            requestFormIssuance={requestFormIssuance}
                                            displaySetting={requestFormIssuanceDisplaySetting}
                                        />
                                        <TableCell align="center">
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => {
                                                    globalState.showConfirm('削除しますか？', () => {
                                                        deleteApi.execute(requestFormIssuance.id!)
                                                    })
                                                }}
                                            >
                                                削除
                                            </Button>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                onClick={() => {
                                                    const url = isHomeTest
                                                        ? `request_forms/${requestFormIssuance.id}/preview_home_test`
                                                        : `request_forms/${requestFormIssuance.id}/preview`

                                                    const historyUrl = `${url}?requestFormIssuanceId=${requestFormIssuance.id}&institutionName=${
                                                        requestFormIssuance.institution?.name
                                                    }&institutionCode=${requestFormIssuance.institution?.code}&shippingCode=${getShippingCode(
                                                        requestFormIssuance
                                                    )}&institutionId=${requestFormIssuance.institutionId}`

                                                    if (vm.createDate() !== today) {
                                                        globalState.showConfirm('依頼書発行日が本日ではありません。依頼書を印刷しますか？', () => {
                                                            props.history.push(historyUrl)
                                                        })
                                                    } else {
                                                        props.history.push(historyUrl)
                                                    }
                                                }}
                                            >
                                                印刷
                                            </Button>
                                        </TableCell>
                                        {isHomeTest && (
                                            <TableCell align="center">
                                                <Button
                                                    size="sm"
                                                    variant="secondary"
                                                    onClick={() => {
                                                        props.history.push(
                                                            `request_forms?requestFormIssuanceId=${requestFormIssuance.id}&institutionId=${requestFormIssuance.institutionId}`
                                                        )
                                                    }}
                                                >
                                                    梱包
                                                </Button>
                                            </TableCell>
                                        )}
                                    </tr>
                                )
                            })}
                        </TableBody>
                    </ExTable>
                    <Pagination pageSet={api.pageSet} response={api.response}></Pagination>
                </TableWrapper>
            </ContentBody>

            <SelectionFixedFooter
                ids={checkIdForm.ids()}
                onClickReset={() => checkIdForm.form.resetForm()}
                searchForm={searchForm}
                searchAttr="id"
                tableName="RequestFormIssuance"
            ></SelectionFixedFooter>
        </>
    )
}

export default withRouter(RequestFormIssuancesPage)
